// Local emulator mode, for the perf suite's web-vitals run (perf/README.md).
//
// Two locks, both required, so a production build can never use it:
//   1. the build was made with REACT_APP_USE_EMULATORS=true (CRA inlines it at
//      build time - `npm run build` for hosting never sets it), and
//   2. the page is being served from this machine (localhost / 127.0.0.1).
// A build with the flag deployed by mistake still talks to production
// Firebase from app.limelightlabs.co.uk, because lock 2 fails there.

export interface EmulatorConfig {
  projectId: string;
  host: string;
  authPort: number;
  firestorePort: number;
  storagePort: number;
  functionsPort: number;
}

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

const port = (value: string | undefined, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

type Env = Record<string, string | undefined>;

export function emulatorConfig(
  hostname: string | undefined = typeof window === 'undefined' ? undefined : window.location.hostname,
  env: Env = process.env,
): EmulatorConfig | null {
  if (env.REACT_APP_USE_EMULATORS !== 'true') return null;
  if (!hostname || !LOCAL_HOSTNAMES.includes(hostname)) return null;
  const projectId = env.REACT_APP_EMULATOR_PROJECT_ID || 'demo-limelight-perf';
  // Never an emulator config that names a real project.
  if (!projectId.startsWith('demo-')) return null;
  return {
    projectId,
    host: '127.0.0.1',
    authPort: port(env.REACT_APP_EMULATOR_AUTH_PORT, 9330),
    firestorePort: port(env.REACT_APP_EMULATOR_FIRESTORE_PORT, 8330),
    storagePort: port(env.REACT_APP_EMULATOR_STORAGE_PORT, 9331),
    // The perf stack serves callables from its instance host, not the
    // Functions emulator (see perf/lib/instance-host.js).
    functionsPort: port(env.REACT_APP_EMULATOR_FUNCTIONS_PORT, 5340),
  };
}
